/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Client-side UI - Weapon loadout container with state management
 * LoadoutPanelContainer.tsx - Container component managing weapon selection state and modal
 */

import type React from "react";
import { useEffect, useState } from "react";
import type { WeaponType } from "@game/config";
import { LoadoutPanel } from "./LoadoutPanel";
import { WeaponSelectionModal } from "./WeaponSelectionModal";
import { loadoutService } from "./loadout.service";

// ============================================================================
// GLOBAL STATE
// ============================================================================

/**
 * Currently selected weapon accessible to client.ts
 * Updated whenever weapon selection changes
 * Provides global access for game initialization
 */
export let selectedWeapon: WeaponType = loadoutService.getCurrentWeapon();

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * LoadoutPanelContainer - Manages weapon selection state and modal
 *
 * **Purpose:**
 * - Container component coordinating LoadoutPanel and WeaponSelectionModal
 * - Manages modal visibility and weapon selection state
 * - Persists weapon selection via loadoutService
 * - Exports global selectedWeapon variable for client.ts access
 *
 * **Architecture:**
 * - Container pattern: manages state, delegates rendering to children
 * - LoadoutPanel: displays current weapon, provides "Change Loadout" button
 * - WeaponSelectionModal: handles weapon selection with stats display
 *
 * **State Management:**
 * - isModalOpen: Controls weapon selection modal visibility
 * - currentWeapon: Currently selected weapon type (for modal highlighting)
 * - selectedWeapon (global): Exported for game initialization access
 *
 * **Persistence Flow:**
 * 1. Mount: Load from localStorage via loadoutService
 * 2. Selection: Save to database + localStorage via modal
 * 3. Update: Sync container state + global variable
 *
 * **Integration Points:**
 * - loadoutService: Persistence layer (localStorage + database)
 * - client.ts: Consumes selectedWeapon for game initialization
 * - LoadoutPanel: Child component for display
 * - WeaponSelectionModal: Child component for selection
 *
 * @example
 * ```tsx
 * // In PreGameUI.tsx
 * <LoadoutPanelContainer />
 *
 * // In client.ts
 * import { selectedWeapon } from './loadout/LoadoutPanelContainer';
 * gameState.player.weapon = selectedWeapon;
 * ```
 */
export const LoadoutPanelContainer: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentWeapon, setCurrentWeapon] = useState<WeaponType>(
    loadoutService.getCurrentWeapon(),
  );

  /**
   * Load weapon selection from storage on mount
   * Updates both local state and global variable
   */
  useEffect(() => {
    const weapon = loadoutService.loadFromStorage();
    setCurrentWeapon(weapon);
    selectedWeapon = weapon;
  }, []);

  /**
   * Handle weapon selection from modal
   *
   * **Flow:**
   * 1. Update local component state
   * 2. Update global selectedWeapon variable
   * 3. Close modal
   *
   * **Note:** Persistence is handled by WeaponSelectionModal
   * via loadoutService.saveToDatabase() before calling this callback
   */
  const handleWeaponSelected = (weapon: WeaponType): void => {
    setCurrentWeapon(weapon);
    selectedWeapon = weapon;
    setIsModalOpen(false);
  };

  /**
   * Handle modal open request from LoadoutPanel
   */
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  /**
   * Handle modal close request (cancel or backdrop click)
   */
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <>
      <LoadoutPanel
        onOpenModal={handleOpenModal}
        currentWeapon={currentWeapon}
      />
      <WeaponSelectionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        currentWeapon={currentWeapon}
        onWeaponSelected={handleWeaponSelected}
      />
    </>
  );
};
